// Added by Sejune Oh on 2019-02-18
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { FC } from 'react'
import { createStyles, withStyles, WithStyles } from '@material-ui/core'
import Fade from '@material-ui/core/Fade'
import classNames from 'classnames'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Define
import {
    ReactInputChangeEvent,
    ReactEmptyEvent,
    ReactInputCheckedEvent,
    ReactKeyUpEvent,
    ReactClickEvent,
} from '@/helper/define'

// Intl
import Intl from '@/helper/Intl'

/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Prime component
import {
    Paper,
    Button,
    FormControlLabel,
    Checkbox,
    Typography,
    Snackbar,
    SnackbarContent,
    TextField,
    InputAdornment,
    AccountCircleIcon,
    LockIcon,
    VisibilityIcon,
    VisibilityOffIcon,
} from '@/components/_primes'

// Style
import {
    LOGIN_FORM_WIDTH,
    LOGIN_BANNER_HEIGHT,
    LOGIN_BOX_HEIGHT,
    LOGIN_BOX_PADDING,
    SNACK_BAR_VALID_TIME,
} from '@/styles/Theme'
import LoginStyle from '@/styles/login/LoginStyle'

/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
export interface LoginFormBaseFCProps {
    savedEmail: string
    loginBtnId: string
    errMessage?: string | null
    dialogType: string
    isChecked: boolean
    isErrorOccured: boolean
    onSnackbarClose: ReactEmptyEvent
    onValueChange: ReactInputChangeEvent
    onCheckedChange: ReactInputCheckedEvent
    onBtnClick: ReactEmptyEvent
    onKeyUp: ReactKeyUpEvent
    hidePassword: boolean
    onPasswordInputIconClick: ReactClickEvent
    version: string
    copyRight: string
    leftSectionBackgroundImage: string
    productBI: string
    useLoginFormTopImage: boolean
    loginTopImage: string
    isOnLoading: boolean
    enableKeepSigninStatus: boolean
    siteName: string
}

interface LoginFormFCProps extends WithStyles<typeof styles>, LoginFormBaseFCProps { }

/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Component
const LoginForm: FC<LoginFormFCProps> = ({ classes, ...props }) => {
    const labelId = Intl.getIntlText({ id: 'htmljs_react.login.loginform.label_id' })
    const labelNameOrEmail = Intl.getIntlText({ id: 'htmljs_react.login.loginform.label_nameoremail' })
    const labelPassword = Intl.getIntlText({ id: 'htmljs_react.login.loginform.label_password' })
    const labelKeepMeLoggedIn = Intl.getIntlText({ id: 'htmljs_react.login.loginform.label_keepmeloggedin' })
    const btnLogIn = Intl.getIntlText({ id: 'htmljs_react.login.loginform.btn_login' })
    const msgInvalidAccount = Intl.getIntlText({ id: 'htmljs_react.login.loginform.msg_invalid_account' })

    return (
        <Paper className={classes.LoginPaper} square={true} elevation={1}>
            <div className={classes.LoginLeftBg} style={{ backgroundImage: props.leftSectionBackgroundImage }}></div>

            <form className={classes.LoginWrapper}>
                <div className={classes.LoginTopWrapper}>
                    <div className={classes.CPWrapper}>
                        {props.useLoginFormTopImage ? (
                            <div
                                className={classes.CPLogoImgWrapper}
                                style={{ backgroundImage: props.loginTopImage }}
                            />
                        ) : (
                            <div className={classNames(classes.CPLogo, classes.CPLogoMain)}>
                                {props.productBI}
                                {props.siteName && <span className={classes.siteName}>{props.siteName}</span>}
                            </div>
            )}
                    </div>
                    <div className={classes.LoginFormWrapper}>
                        <TextField
                            className={classes.LoginFormInput}
                            id="email"
                            label={labelId}
                            placeholder={labelNameOrEmail}
                            variant="outlined"
                            type="text"
                            name="email"
                            onChange={props.onValueChange}
                            autoComplete="email nickname"
                            autoFocus={!props.savedEmail}
                            defaultValue={props.savedEmail}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <AccountCircleIcon />
                                    </InputAdornment>
                                ),
                                onKeyUp: props.onKeyUp,
                            }}
                            inputProps={{
                                className: classes.AutoFocusableInput,
                                'data-cy-input': 'id',
                                'data-testid': 'id'
                            }}
                        />
                        <TextField
                            id="password"
                            label={labelPassword}
                            className={classes.LoginFormInput}
                            variant="outlined"
                            name="password"
                            onChange={props.onValueChange}
                            autoComplete="current-password"
                            autoFocus={!!props.savedEmail}
                            type={props.hidePassword ? 'password' : 'text'}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="start" onClick={props.onPasswordInputIconClick}>
                                        {props.hidePassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                    </InputAdornment>
                                ),
                                onKeyUp: props.onKeyUp,
                            }}
                            inputProps={{
                                className: classes.AutoFocusableInput,
                                'data-cy-input': 'pw',
                                'data-testid': 'pw'
                            }}
                        />
                        {props.enableKeepSigninStatus && (
                            <FormControlLabel
                                className={classes.LoginFormCtrLabel}
                                onChange={props.onCheckedChange}
                                control={<Checkbox
                                    checked={props.isChecked}
                                    value="remember"
                                    color="primary"
                                    inputProps={{
                                        id: "cb-keepmeloggedin"
                                    }}
                                    data-cy-check={'keepmeloggedin'}
                                />}
                                label={labelKeepMeLoggedIn}
                            />
                        )}
                    </div>
                </div>
                <div className={classes.LoginBtnWrapper}>
                    <Button
                        id={props.loginBtnId}
                        btnText={btnLogIn}
                        className={classes.LoginBtn}
                        buttonType="task"
                        onClick={props.onBtnClick}
                        disabled={props.isOnLoading}
                        data-cy-btn={'submit'}
                        data-testid={'submit'}
                    />
                </div>
            </form>

            <Typography className={classes.LoginPaperCopyRight} align="center">
                {props.copyRight} ({props.version})
            </Typography>

            {/** Snack bar for Login Status */}
            <Snackbar
                className={
                    props.dialogType==='' ?
                    classes.LoginSnackbar :
                    props.dialogType==='twoFactorRegister' ?
                       classes.TwofactorRegisterSnackbar :
                       classes.TwofactorAuthSnackbar
                    }
                open={props.isErrorOccured}
                onClose={props.onSnackbarClose}
                TransitionComponent={Fade}
                autoHideDuration={SNACK_BAR_VALID_TIME}
            >
                <SnackbarContent
                    className={classes.LoginSnackbarContent}
                    evtType="error"
                    noticeFrom="signIn"
                    message={props.errMessage || msgInvalidAccount}
                    onClose={props.onSnackbarClose}
                />
            </Snackbar>
        </Paper>
    )
}

/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Styles
const styles = createStyles({
    ...LoginStyle(),
    LoginPaper: {
        backgroundColor: '#292a2c',
        width: `${LOGIN_FORM_WIDTH}px`,
        height: `${LOGIN_BANNER_HEIGHT + LOGIN_BOX_HEIGHT}px`,
        position: 'relative',
        display: 'flex',
    },
    LoginLeftBg: {
        display: 'flex',
        flexBasis: '160px',
        backgroundSize: 'auto 100%',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
    },
    LoginWrapper: {
        display: 'flex',
        flex: '1',
        flexDirection: 'column',
        padding: `${LOGIN_BOX_PADDING}px`,
        position: 'relative',
        justifyContent: 'space-between',
        maxWidth: '440px'
    },
    LoginTopWrapper: {},
    LoginBottomWrapper: {},
    CPWrapper: {
        display: 'flex',
        alignItems: 'baseline',
        flexDirection: 'column',
        maxWidth: '392px',
        maxHeight: '114px',
    },
    CPLogoMain: {
        fontSize: '40px',
        maxWidth: 'inherit',
    },
    CPLogoImgWrapper: {
        height: '46px',
        width: '100%',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'auto 100%',
    },
    LoginFormWrapper: {
        display: 'flex',
        flexDirection: 'column',
        marginTop: 18,
    },
    LoginBtnWrapper: {},
    LoginBtn: {
        height: '45px',
        fontSize: '1.4rem',
        // marginBottom: '35px',
        width: '100% !important',
    },
    LoginSnackbar: {
        position: 'absolute',
        width: '100%',
        bottom: '-60px',
    },
    TwofactorRegisterSnackbar: {
        position: 'absolute',
        width: '83%',
        bottom: '-140px',
    },
    TwofactorAuthSnackbar: {
        position: 'absolute',
        width: '83%',
        bottom: '10px',
    },
    LoginFormInput: {
        marginTop: '24px',
        marginBottom: '16px',
        '& label': {
            fontSize: '1.3rem',
            fontWeight: 'normal'
        },
        '& label.Mui-focused': {
            fontWeight: 'bold'
        }
    },
    LoginFormCtrLabel: {
        '& span': {
            fontSize: '1.1rem'
        }
    },
    siteName: {
        maxWidth: '396px',
        fontSize: '2rem',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        display: 'inline-block',
        marginLeft: '1.5rem',
        verticalAlign: 'sub',
        lineHeight: '3.4rem',
        WebkitTextFillColor: '#9EC1D4',
        fontWeight: 'normal'
    }
})

export default withStyles(styles)(LoginForm)
